import { FC, useState } from "react";
import WebcamCard from "../../../components/WebcamCard";
import { useAnalysisFlow, useLoading, useStore, useToast } from "../../../contexts";
import {
	MdKeyboardDoubleArrowLeft,
	MdKeyboardDoubleArrowRight,
	MdCameraAlt,
	MdNavigateNext,
	MdNavigateBefore,
	MdCheck
} from "react-icons/md";
import { ImageDataObject, payloadOfFootStorApi } from "../../../types/other.types";
import API from "../../../services/api";

const WebcamPage: FC = () => {
	const { footcareAnalysis, setFootcareAnalysis, handlePrevClick, handleNextClick } = useAnalysisFlow();
	console.log(footcareAnalysis);
	const { setLoading } = useLoading();
	const { showToast } = useToast();
	const { storeData } = useStore();
	const store = storeData?.store;
	const videoWidth = storeData?.cameraSettings.width;
	const videoHeight = storeData?.cameraSettings.height;

	// Sequential capture state management
	const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
	const [capturedImages, setCapturedImages] = useState<ImageDataObject>({
		leftSide: "",
		rightSide: "",
		leftTop: "",
		rightTop: ""
	});
	const [showReviewScreen, setShowReviewScreen] = useState(false);

	// Camera configuration array
	const cameraConfigs = [
		{
			key: "leftSide" as keyof ImageDataObject,
			deviceId: storeData?.cameraSettings.leftSide,
			verticalValue: storeData?.cameraSettings.leftSideVerticalValue,
			horizontalValue: storeData?.cameraSettings.leftSideHorizentalValue,
			title: "Left Side",
			dotP1: storeData?.cameraSettings.leftSideDOT1,
			dotP2: storeData?.cameraSettings.leftSideDOT2
		},
		{
			key: "leftTop" as keyof ImageDataObject,
			deviceId: storeData?.cameraSettings.leftTop,
			verticalValue: storeData?.cameraSettings.leftTopVerticalValue,
			horizontalValue: storeData?.cameraSettings.leftTopHorizentalValue,
			title: "Left Top",
			dotP1: storeData?.cameraSettings.leftTopDOT1,
			dotP2: storeData?.cameraSettings.leftTopDOT2
		},
		{
			key: "rightTop" as keyof ImageDataObject,
			deviceId: storeData?.cameraSettings.rightTop,
			verticalValue: storeData?.cameraSettings.rightTopVerticalValue,
			horizontalValue: storeData?.cameraSettings.rightTopHorizentalValue,
			title: "Right Top",
			dotP1: storeData?.cameraSettings.rightTopDOT1,
			dotP2: storeData?.cameraSettings.rightTopDOT2
		},
		{
			key: "rightSide" as keyof ImageDataObject,
			deviceId: storeData?.cameraSettings.rightSide,
			verticalValue: storeData?.cameraSettings.rightSideVerticalValue,
			horizontalValue: storeData?.cameraSettings.rightSideHorizentalValue,
			title: "Right Side",
			dotP1: storeData?.cameraSettings.rightSideDOT1,
			dotP2: storeData?.cameraSettings.rightSideDOT2
		}
	];

	const currentCamera = cameraConfigs[currentCameraIndex];

	const sendImagesToApi = async (imageDataObject: ImageDataObject) => {
		setLoading(true);
		try {
			const payloadOfFootStorApi: payloadOfFootStorApi = {
				Left_Side: imageDataObject.leftSide.replace("data:image/png;base64,", ""),
				Right_Side: imageDataObject.rightSide.replace("data:image/png;base64,", ""),
				Left_Top: imageDataObject.leftTop.replace("data:image/png;base64,", ""),
				Right_Top: imageDataObject.rightTop.replace("data:image/png;base64,", ""),
				name: footcareAnalysis.name,
				Sex: footcareAnalysis.gender,
				min_age: footcareAnalysis.minimum_age,
				max_age: footcareAnalysis.maximum_age,
				left_adjustment: storeData?.cameraSettings.leftAdjustment,
				right_adjustment: storeData?.cameraSettings.rightAdjustment,
				leftPPU: storeData?.cameraSettings.leftPPU,
				rightPPU: storeData?.cameraSettings.rightPPU,
				group_number: store.group_number,
				group_name: store.group_name,
				store_number: store.store_number,
				store_name: store.store_name,
				dev_id: store.machine_id,
				weight: footcareAnalysis.weight,
				height: footcareAnalysis.height,
				right_top_horizontal_value: storeData?.cameraSettings.rightTopHorizentalValue,
				right_top_vertical_value: storeData?.cameraSettings.rightTopVerticalValue,
				right_side_horizontal_value: storeData?.cameraSettings.rightSideHorizentalValue,
				right_side_vertical_value: storeData?.cameraSettings.rightSideVerticalValue,
				left_top_horizontal_value: storeData?.cameraSettings.leftTopHorizentalValue,
				left_top_vertical_value: storeData?.cameraSettings.leftTopVerticalValue,
				left_side_horizontal_value: storeData?.cameraSettings.leftSideHorizentalValue,
				left_side_vertical_value: storeData?.cameraSettings.leftSideVerticalValue,
				leftTopDots: {
					dot1: storeData?.cameraSettings.leftTopDOT1,
					dot2: storeData?.cameraSettings.leftTopDOT2
				},
				rightTopDots: {
					dot1: storeData?.cameraSettings.rightTopDOT1,
					dot2: storeData?.cameraSettings.rightTopDOT2
				},
				leftSideDots: {
					dot1: storeData?.cameraSettings.leftSideDOT1,
					dot2: storeData?.cameraSettings.leftSideDOT2
				},
				rightSideDots: {
					dot1: storeData?.cameraSettings.rightSideDOT1,
					dot2: storeData?.cameraSettings.rightSideDOT2
				}
			};

			console.log(payloadOfFootStorApi);
			// Send the POST request using Axios with the cleaned image data
			const response = await API.scan(payloadOfFootStorApi);
			console.log("Images sent successfully!", response);
			// const data = response.data;
			if (!response.Left_Length && !response.Right_Length) {
				showToast("Error processing images. Please try again.", "error");
				return;
			}
			setFootcareAnalysis((prevFootcareAnalysis: any) => ({
				...prevFootcareAnalysis,
				left_length: response["Left_Length"],
				left_width: response["Left_Width"],
				left_instep_height: response["Left_Instep_Height"],
				left_arch_height: response["Left_Arch_Height"],
				right_length: response["Right_Length"],
				right_width: response["Right_Width"],
				right_instep_height: response["Right_Instep_Height"],
				right_arch_height: response["Right_Arch_Height"],
				left_shoe_size: response["Left_shoe_size_UK"],
				right_shoe_size: response["Right_shoe_size_UK"],
				left_arch_type: response["Arch_Type_Left"],
				right_arch_type: response["Arch_Type_Right"],
				left_shoe_size_eu: response["Left_shoe_size_EU"],
				right_shoe_size_eu: response["Right_shoe_size_EU"],
				right_width_type: response["Width_Type_Right"],
				left_width_type: response["Width_Type_Left"],
				scan_id: response["scan_id"],
				scan_object_id: response["scan_object_id"],
				ppu_left: response["ppu_left"],
				ppu_right: response["ppu_right"],
				adj_right: response["adj_right"],
				adj_left: response["adj_left"],
				left_heel_width: response["Left_Heel_Width"],
				right_heel_width: response["Right_Heel_Width"],
				marker_distance_left: response["Marker_Distance_Left"],
				marker_distance_right: response["Marker_Distance_Right"],
				left_ahi: response["left_AHI"],
				right_ahi: response["right_AHI"]
			}));
			handleNextClick();
			// You can handle the API response here
		} catch (error) {
			console.log("Error sending images:", error);
			showToast("Failed to process images. Please check your connection and try again.", "error");
			// Handle errors here
		} finally {
			setLoading(false);
		}
	};

	// Individual camera capture function
	const captureCurrentCamera = async () => {
		setLoading(true);
		try {
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");

			if (ctx && currentCamera) {
				const video = document.createElement("video");
				video.autoplay = true;
				video.playsInline = true;

				const stream = await navigator.mediaDevices.getUserMedia({
					video: {
						deviceId: { exact: currentCamera.deviceId },
						width: storeData?.cameraSettings.width,
						height: storeData?.cameraSettings.height
					}
				});
				video.srcObject = stream;

				await new Promise<void>((resolve) => {
					video.onloadedmetadata = () => {
						canvas.width = video.videoWidth;
						canvas.height = video.videoHeight;
						ctx.drawImage(video, 0, 0);
						const base64Image = canvas.toDataURL("image/png");

						// Update captured images
						setCapturedImages((prev) => ({
							...prev,
							[currentCamera.key]: base64Image
						}));

						stream.getTracks().forEach((track) => track.stop());
						resolve();
					};
				});
			}
		} catch (error) {
			console.error(`Error capturing ${currentCamera?.title}:`, error);
		}
		setLoading(false);
	};

	// Navigation functions
	const handlePrevCamera = () => {
		if (currentCameraIndex > 0) {
			setCurrentCameraIndex(currentCameraIndex - 1);
		}
	};

	const handleNextCamera = () => {
		if (currentCameraIndex < cameraConfigs.length - 1) {
			setCurrentCameraIndex(currentCameraIndex + 1);
		} else if (allCamerasCaptured()) {
			setShowReviewScreen(true);
		}
	};

	// Check if current camera is captured
	const isCurrentCameraCaptured = () => {
		return capturedImages[currentCamera?.key] !== "";
	};

	// Check if all cameras are captured
	const allCamerasCaptured = () => {
		return Object.values(capturedImages).every((image) => image !== "");
	};

	// Retake specific camera
	const retakeCamera = (cameraKey: keyof ImageDataObject) => {
		setCapturedImages((prev) => ({
			...prev,
			[cameraKey]: ""
		}));
		const cameraIndex = cameraConfigs.findIndex((config) => config.key === cameraKey);
		setCurrentCameraIndex(cameraIndex);
		setShowReviewScreen(false);
	};

	// Continue to next step
	const handleContinue = () => {
		sendImagesToApi(capturedImages);
	};

	return (
		<div className="relative h-full w-full">
			<div className="sub-header text-center bg-secondary text-white">
				Please Stand Steady For Capture Your Foot
			</div>
			{!showReviewScreen ? (
				// Sequential capture view
				<div className="w-full flex flex-col items-center justify-center h-[90%] py-5% overflow-auto">
					{/* Camera progress indicator */}
					<div className="flex items-center justify-center mb-4 space-x-2">
						{cameraConfigs.map((config, index) => (
							<div key={config.key} className="flex items-center">
								<div
									className={`w-3 h-3 rounded-full ${
										index === currentCameraIndex
											? "bg-blue-500"
											: capturedImages[config.key]
											? "bg-green-500"
											: "bg-gray-300"
									}`}
								/>
								{index < cameraConfigs.length - 1 && <div className="w-8 h-0.5 bg-gray-300 mx-1" />}
							</div>
						))}
					</div>

					{/* Current camera title */}
					<h2 className="text-xl font-semibold mb-4 text-center">
						{currentCamera?.title} ({currentCameraIndex + 1}/{cameraConfigs.length})
					</h2>

					{/* Single camera view */}
					<div className="flex justify-center">
						<WebcamCard
							selectedDeviceId={currentCamera?.deviceId}
							verticalValue={currentCamera?.verticalValue}
							horizontalValue={currentCamera?.horizontalValue}
							videoHeight={videoHeight}
							videoWidth={videoWidth}
							title={currentCamera?.title}
							dotP1={currentCamera?.dotP1}
							dotP2={currentCamera?.dotP2}
						/>
					</div>

					{/* Capture button */}
					<div className="mt-6">
						<button
							onClick={captureCurrentCamera}
							className={`px-8 py-3 rounded-lg font-semibold flex items-center space-x-2 ${
								isCurrentCameraCaptured()
									? "bg-green-500 hover:bg-green-600 text-white"
									: "bg-blue-500 hover:bg-blue-600 text-white"
							}`}
						>
							{isCurrentCameraCaptured() ? (
								<>
									<MdCheck className="text-xl" />
									<span>Captured - Click to Retake</span>
								</>
							) : (
								<>
									<MdCameraAlt className="text-xl" />
									<span>Capture {currentCamera?.title}</span>
								</>
							)}
						</button>
					</div>

					{/* Navigation buttons */}
					<div className="flex justify-between w-full max-w-md mt-8">
						<button
							onClick={handlePrevCamera}
							disabled={currentCameraIndex === 0}
							className={`px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 ${
								currentCameraIndex === 0
									? "bg-gray-300 text-gray-500 cursor-not-allowed"
									: "bg-[#60cf63] hover:bg-[#50bf53] text-white"
							}`}
						>
							<MdNavigateBefore className="text-xl" />
							<span>Previous</span>
						</button>

						<button
							onClick={handleNextCamera}
							disabled={!isCurrentCameraCaptured() && currentCameraIndex < cameraConfigs.length - 1}
							className={`px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 ${
								!isCurrentCameraCaptured() && currentCameraIndex < cameraConfigs.length - 1
									? "bg-gray-300 text-gray-500 cursor-not-allowed"
									: "bg-[#60cf63] hover:bg-[#50bf53] text-white"
							}`}
						>
							<span>
								{currentCameraIndex === cameraConfigs.length - 1 && allCamerasCaptured()
									? "Review All"
									: "Next"}
							</span>
							<MdNavigateNext className="text-xl" />
						</button>
					</div>
				</div>
			) : (
				// Review screen
				<div className="w-full flex flex-col items-center justify-center h-[90%] py-5% overflow-auto">
					<h2 className="text-2xl font-semibold mb-6 text-center">Review All Captures</h2>

					{/* All captured images grid */}
					<div className="grid grid-cols-2 gap-6 mb-6">
						{cameraConfigs.map((config) => (
							<div key={config.key} className="text-center">
								<h3 className="font-semibold mb-3 text-lg">{config.title}</h3>
								{capturedImages[config.key] ? (
									<div className="relative">
										<img
											src={capturedImages[config.key]}
											alt={config.title}
											className="w-80 h-60 object-cover rounded-lg border-2 border-gray-300 shadow-md"
										/>
										<button
											onClick={() => retakeCamera(config.key)}
											className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-semibold shadow-lg"
										>
											Retake
										</button>
									</div>
								) : (
									<div className="w-80 h-60 bg-gray-200 rounded-lg border-2 border-dashed border-gray-400 flex items-center justify-center">
										<span className="text-gray-500 text-lg">Not captured</span>
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			)}

			<div className="bottom-pointer-left" onClick={handlePrevClick}>
				<MdKeyboardDoubleArrowLeft className="hover:scale-90 text-3xl" />
				<span className="text-[1.5vw] font-bold ml-3">Back</span>
			</div>
			<div className="bottom-pointer-right" onClick={handleContinue}>
				<span className="text-[1.5vw] font-bold">Next</span>
				<MdKeyboardDoubleArrowRight className="hover:scale-90 text-3xl ml-3" />
			</div>
		</div>
	);
};

export default WebcamPage;
